import React from 'react';
import { Card } from 'primereact/card';
import { Divider } from 'primereact/divider';

import usePageTitle from '../hooks/UsePageTitle';
const AboutUs = () => {
  usePageTitle('About Us | SnP Finvisors');

  return (
    <div className="page-container">
      <div className="grid">
        {/* Hero Section */}
        <div className="col-12">
          <div className="hero-section p-5 border-round mb-5" style={{ 
            background: 'linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url(https://images.unsplash.com/photo-1450101499163-c8848c66ca85)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            color: 'white'
          }}>
            <h1 className="text-5xl font-bold mb-3">About SnP Finvisors</h1>
            <p className="text-xl line-height-3">
              Empowering businesses with financial clarity and strategic growth solutions
            </p>
          </div>
        </div>

        {/* Introduction Section */}
        <div className="col-12">
          <div className="text-center mb-6">
            <h2 className="text-3xl mb-3">About Finvisors</h2>
            <Divider className="w-4 mx-auto" />
            <p className="text-xl line-height-3 mx-auto" style={{ maxWidth: '800px' }}>
              At SnP Finvisors, we are a team of dynamic and experienced  Chartered Accountants and Finance Professionals with a passion for driving business excellence. 
              We're dedicated to empowering businesses with expert financial and business solutions tailored to their unique needs.
            </p>
          </div>
        </div>

        {/* Team Section */}
        <div className="col-12 md:col-6">
          <img 
            src='/images/tax4.jpg'
            alt="SnP Finvisors Team" 
            className="w-full border-round shadow-2" 
            style={{ maxHeight: '500px', objectFit: 'cover' }}
          />
        </div>
        <div className="col-12 md:col-6">
          <Card className="h-full">
            <h3 className="text-2xl mt-0">Our Story</h3>
            <Divider />
            <p className="text-lg line-height-3">
              SnP Finvisors was founded with a clear goal—to simplify financial management, enhance business efficiency, 
              and support sustainable growth through innovative strategies and process automation.
            </p>
            <p className="text-lg line-height-3 mt-3">
              With extensive experience across various industries, we understand the unique challenges faced by small and 
              medium-scale enterprises (SMEs). Our approach combines technical expertise with practical business acumen.
            </p>
            <p className="text-lg line-height-3 mt-3 font-semibold">
              We believe in delivering quality-driven, innovative, and customized solutions that enable businesses to 
              streamline operations, enhance efficiency, and achieve sustainable growth.
            </p>
          </Card>
        </div>

        {/* Mission & Vision */}
        <div className="col-12 mt-6">
          <div className="grid">
            {/* Mission */}
            <div className="col-12 md:col-6">
              <Card className="h-full">
                <div className="flex flex-column align-items-center text-center">
                  <img 
                    src='/images/tax3.jpg' 
                    alt="Our Mission" 
                    className="w-full border-round mb-3" 
                    style={{ maxHeight: '250px', objectFit: 'cover' }}
                  />
                  <h3 className="text-2xl mt-0">Our Mission</h3>
                  <Divider className="w-4" />
                  <p className="text-lg line-height-3">
                    To empower SMEs by providing comprehensive financial, compliance, and business automation solutions. 
                    We strive to be their trusted growth partner, enabling them to focus on their core business while we 
                    seamlessly manage their financial processes and regulatory requirements.
                  </p>
                </div>
              </Card>
            </div>

            {/* Vision */}
            <div className="col-12 md:col-6">
              <Card className="h-full">
                <div className="flex flex-column align-items-center text-center">
                  <img 
                    src='/images/tax2.jpg' 
                    alt="Our Vision" 
                    className="w-full border-round mb-3" 
                    style={{ maxHeight: '250px', objectFit: 'cover' }}
                  />
                  <h3 className="text-2xl mt-0">Our Vision</h3>
                  <Divider className="w-4" />
                  <p className="text-lg line-height-3">
                    To be a pioneer in delivering one-stop business solutions that transform and elevate SMEs. We envision 
                    a future where businesses thrive with seamless financial and operational support, leveraging our expertise 
                    to navigate challenges, embrace technology, and achieve long-term growth.
                  </p>
                </div>
              </Card>
            </div>
          </div>
        </div>

        {/* Values Section */}
        <div className="col-12 mt-6">
          <h2 className="text-center text-3xl mb-5">Our Core Values</h2>
          <div className="grid">
            <div className="col-12 md:col-4">
              <Card className="text-center h-full">
                <i className="pi pi-star-fill text-primary text-5xl mb-3"></i>
                <h3 className="text-xl mt-0">Excellence</h3>
                <p>
                  We are committed to delivering exceptional quality in all our services, continuously improving to meet 
                  and exceed client expectations.
                </p>
              </Card>
            </div>
            <div className="col-12 md:col-4">
              <Card className="text-center h-full">
                <i className="pi pi-lock text-primary text-5xl mb-3"></i>
                <h3 className="text-xl mt-0">Integrity</h3>
                <p>
                  We uphold the highest ethical standards, ensuring transparency, honesty, and confidentiality in all 
                  our client relationships.
                </p>
              </Card>
            </div>
            <div className="col-12 md:col-4">
              <Card className="text-center h-full">
                <i className="pi pi-bolt text-primary text-5xl mb-3"></i>
                <h3 className="text-xl mt-0">Innovation</h3>
                <p>
                  We embrace technology and creative thinking to develop forward-looking solutions that drive business 
                  efficiency and growth.
                </p>
              </Card>
            </div>
          </div>
        </div>

        {/* Promise Section */}
        <div className="col-12 mt-6">
          <Card className="surface-100">
            <h2 className="text-center text-3xl mt-0">Our Commitment</h2>
            <Divider className="w-4 mx-auto" />
            <div className="grid">
              <div className="col-12 md:col-6">
                <div className="flex align-items-start mb-4">
                  <i className="pi pi-check-circle text-primary text-2xl mr-3 mt-1"></i>
                  <div>
                    <h4 className="mt-0 mb-2">Comprehensive Solutions</h4>
                    <p>
                      From accounting and compliance to strategic financial consulting and automation, we aim to be a 
                      one-stop solution for all your business needs.
                    </p>
                  </div>
                </div>
              </div>
              <div className="col-12 md:col-6">
                <div className="flex align-items-start mb-4">
                  <i className="pi pi-check-circle text-primary text-2xl mr-3 mt-1"></i>
                  <div>
                    <h4 className="mt-0 mb-2">Client-Centric Approach</h4>
                    <p>
                      Your success is our priority. We take the time to understand your unique challenges and tailor 
                      our services accordingly.
                    </p>
                  </div>
                </div>
              </div>
              <div className="col-12 md:col-6">
                <div className="flex align-items-start mb-4">
                  <i className="pi pi-check-circle text-primary text-2xl mr-3 mt-1"></i>
                  <div>
                    <h4 className="mt-0 mb-2">Technology-Driven</h4>
                    <p>
                      We leverage cutting-edge tools and automation to streamline your financial processes and provide 
                      real-time insights.
                    </p>
                  </div>
                </div>
              </div>
              <div className="col-12 md:col-6">
                <div className="flex align-items-start">
                  <i className="pi pi-check-circle text-primary text-2xl mr-3 mt-1"></i>
                  <div>
                    <h4 className="mt-0 mb-2">Growth Partnership</h4>
                    <p>
                      We don't just provide services—we become your strategic partner in achieving sustainable business 
                      growth and success.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AboutUs;